import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import api from '@/app/api/axios.js';
import { unexpectedError } from '@/shared/utils/unexpected-error.js';
import { cloneDeep } from 'lodash-es';
import { SupportStatus } from '#modules/support/models/SupportStatus.js';
import { clearSupportAvatarCache } from '#modules/support/composables/useSupportMessageReplies.js';

export const useSupportMessageStore = defineStore('support-message', () => {
    const supportMessage = ref(null);
    const replyAttachments = ref([]);

    const hitAttachmentsLimit = computed(() => {
        return replyAttachments.value.length >= 5;
    });

    const setActiveSupportMessage = (newSupportMessage) => {
        // Reset the reply attachments when setting a new support message
        if (supportMessage.value && supportMessage.value.id !== newSupportMessage.id) {
            replyAttachments.value = [];
        }

        supportMessage.value = cloneDeep(newSupportMessage);

        void markAsRead();
    };

    const incrementRepliesCount = () => {
        if (!supportMessage.value) {
            return;
        }

        // Reopen support message if it was closed
        if (supportMessage.value.attributes.status === SupportStatus.Closed) {
            supportMessage.value.attributes.status = SupportStatus.WaitingForUser;
        }

        // Set new replies count for support message
        supportMessage.value.attributes.messages_count += 1;
    };

    const markAsRead = async () => {
        if (!supportMessage.value) {
            return;
        }

        supportMessage.value.attributes.read_at = new Date().toISOString();

        try {
            await api.post(`/general/support-messages/${supportMessage.value.id}/read`);
        } catch (e) {
            unexpectedError(e);
        }
    };

    const closeTicket = async () => {
        if (!supportMessage.value) {
            return false;
        }

        if (supportMessage.value.attributes.status === SupportStatus.Closed) {
            return true;
        }

        try {
            await api.post(`/general/support-messages/${supportMessage.value.id}/close`);

            supportMessage.value.attributes.status = SupportStatus.Closed;

            return true;
        } catch (e) {
            unexpectedError(e);

            return false;
        }
    };

    const addAttachment = (attachment) => {
        replyAttachments.value.push(attachment);
    };

    const removeAttachment = (index) => {
        replyAttachments.value.splice(index, 1);
    };

    const resetAttachments = () => {
        replyAttachments.value = [];
    };

    const dehydrate = () => {
        supportMessage.value = null;
        replyAttachments.value = [];
        clearSupportAvatarCache();
    };

    return {
        supportMessage,
        replyAttachments,
        hitAttachmentsLimit,
        setActiveSupportMessage,
        incrementRepliesCount,
        markAsRead,
        closeTicket,
        addAttachment,
        removeAttachment,
        resetAttachments,
        dehydrate,
    };
});
